import { useEffect, useMemo, useState } from 'react'
import { View, Text, TextInput, TouchableOpacity, StyleSheet, KeyboardAvoidingView, Platform, ScrollView, Image } from 'react-native'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { supabase } from '../lib/supabase'
import { colors, fonts } from '../lib/theme'
import { useThemeColors } from '../lib/useThemeColors'
import GradientButton from '../components/GradientButton'
import { LOGO } from '../lib/assets'

export default function ResetPassword() {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const router = useRouter()

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [show, setShow] = useState(false)
  const [loading, setLoading] = useState(false)
  const [msg, setMsg] = useState(null)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let mounted = true

    // Supabase imposta la sessione automaticamente dal token nell'URL (deep
    // link). Su web il token nel frammento (#access_token=...) viene letto
    // e "consumato" dal client Supabase al momento della sua creazione
    // (all'avvio app, in lib/supabase.js) — PRIMA che questo componente
    // monti. Quindi l'evento PASSWORD_RECOVERY può già essere stato
    // sparato e perso: onAuthStateChange da solo non basta, serve anche
    // il controllo diretto della sessione già esistente come fallback.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setReady(true)
      }
    })

    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (mounted && session) {
        setReady(true)
      }
    })

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [])

  const handleReset = async () => {
    if (password.length < 6) { setMsg({ type: 'error', text: t('profile.pass_min_length') }); return }
    if (password !== confirm) { setMsg({ type: 'error', text: t('profile.pass_mismatch') }); return }
    setLoading(true); setMsg(null)
    try {
      const { error } = await supabase.auth.updateUser({ password })
      if (error) throw error
      setMsg({ type: 'success', text: t('reset_password.success') })
      setTimeout(() => router.replace('/'), 2000)
    } catch (e) {
      setMsg({ type: 'error', text: e.message })
    } finally { setLoading(false) }
  }

  return (
    <KeyboardAvoidingView
      style={[styles.container, { backgroundColor: tc.bg }]}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <ScrollView contentContainerStyle={styles.scroll} keyboardShouldPersistTaps="handled">

        {/* Header */}
        <View style={styles.header}>
          <Image
            source={LOGO}
            style={styles.logo}
            resizeMode="contain"
          />
          <Text style={[styles.title, { color: tc.text }]}>{t('reset_password.title')}</Text>
          <Text style={[styles.subtitle, { color: tc.textMuted }]}>{t('reset_password.subtitle')}</Text>
        </View>

        {/* Card */}
        <View style={[styles.card, { backgroundColor: tc.bgCard, borderColor: tc.border }]}>
          {!ready ? (
            <Text style={[styles.checking, { color: tc.textMuted }]}>{t('reset_password.loading')}</Text>
          ) : (
            <>
              {msg && (
                <View style={[styles.alert, {
                  backgroundColor: msg.type === 'success' ? 'rgba(77,200,120,0.1)' : tc.rosaSubtle,
                  borderColor: msg.type === 'success' ? 'rgba(77,200,120,0.2)' : tc.rosaBorder,
                }]}>
                  <Text style={[styles.alertText, { color: msg.type === 'success' ? '#4dc878' : tc.rosaText }]}>
                    {msg.text}
                  </Text>
                </View>
              )}

              <TextInput
                style={[styles.input, { backgroundColor: tc.bgCard2, borderColor: tc.border, color: tc.text }]}
                placeholder={t('profile.new_pass_placeholder')}
                placeholderTextColor={tc.textDim}
                value={password}
                onChangeText={setPassword}
                secureTextEntry={!show}
                autoCapitalize="none"
                autoCorrect={false}
              />
              <TextInput
                style={[styles.input, { backgroundColor: tc.bgCard2, borderColor: tc.border, color: tc.text }]}
                placeholder={t('profile.confirm_password')}
                placeholderTextColor={tc.textDim}
                value={confirm}
                onChangeText={setConfirm}
                secureTextEntry={!show}
                autoCapitalize="none"
                autoCorrect={false}
                onSubmitEditing={handleReset}
              />

              {/* Mostra password */}
              <TouchableOpacity style={styles.showRow} onPress={() => setShow(s => !s)}>
                <View style={[styles.checkbox, { borderColor: show ? tc.rosa : tc.border, backgroundColor: show ? tc.rosa : 'transparent' }]}>
                  {show && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <Text style={[styles.showLabel, { color: tc.textMuted }]}>{t('reset_password.show_pass')}</Text>
              </TouchableOpacity>

              <GradientButton
                onPress={handleReset}
                loading={loading}
                disabled={!password || !confirm}
                size="lg"
                style={{ width: '100%' }}
              >
                {t('profile.update_pass')}

              </GradientButton>
            </>
          )}
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  container: { flex: 1 },
  scroll: { flexGrow: 1, justifyContent: 'center', padding: 24 },

  header: { alignItems: 'center', marginBottom: 28 },
  logo: { width: 64, height: 64, marginBottom: 12 },
  title: { fontFamily: fonts.condensedBold, fontSize: 26, textAlign: 'center' },
  subtitle: { fontSize: 12, marginTop: 8, textAlign: 'center' },

  card: { borderRadius: 16, borderWidth: 1, padding: 28 },
  checking: { textAlign: 'center', fontSize: 13 },

  alert: { borderWidth: 1, borderRadius: 8, padding: 10, paddingHorizontal: 14, marginBottom: 16 },
  alertText: { fontSize: 13, textAlign: 'center' },

  input: { borderWidth: 1, borderRadius: 8, padding: 12, paddingHorizontal: 14, fontSize: 14, marginBottom: 12 },

  showRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 16 },
  checkbox: { width: 18, height: 18, borderRadius: 4, borderWidth: 1, alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  checkmark: { color: tc.brandText, fontSize: 11, lineHeight: 13 },
  showLabel: { fontSize: 12 },
})